import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import { PercentFormat, FormatISODate } from '../../../helpers/formatting'
import { handleErrorResponse, generateErrorBody } from '../../../helpers/errorHandling'
import { format } from 'date-fns'
import ManagerApprovalIcons from '../../../components/elements/ManagerApprovalIcons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import PaymentDatePicker from '../../../components/PaymentDatePicker'
import { getManagerApprovalStatus, getFundingMessage, computeScheduleAdjustments, getOfferedPaymentAmount, buildIdealPaymentAmountOptions } from './verificationPaymentScheduleHelpers'
import PaymentScheduleTable from './PaymentScheduleTable'
import CustomerDeclinedModal from './CustomerDeclinedModal'

/**
 * Step 2 of verifications process. Enables the Agent to create the initial payment schedule and verify Loan Amount,
 * APR and interest rate.
 * @constructor
 */
export default function VerificationPaymentSchedule(props) {
  const {
    setStep,
    customerData,
    paymentScheduleDetails,
    programs,
    setLoanData,
    paymentMethods,
    ptiTerms,
    setPtiTerms,
    paymentScheduleData,
    setPaymentScheduleData,
    dropdownOptions,
    verificationId,
    managerApproval,
    setManagerApproval,
    setManagerApprovalPoll,
    isReadOnly,
    isPrequalified,
    prequalifiedData,
    isRealtimeFundingAvailable,
    setIsRealtimeFundingAvailable,
    originalEffectiveDate,
    originalLoanFundingMethodId,
  } = props;
  const dispatch = useDispatch()
  const requests = useRequest()
  const { register, handleSubmit, control, setError, formState: { errors, isDirty }, watch, setValue } = useForm();
  const [paymentScheduleFormData, setPaymentScheduleFormData] = useState({});
  const today = format(new Date(), 'yyyy-MM-dd');

  const [hasFormError, setHasFormError] = useState(false)
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([])
  const [paymentMethod, setPaymentMethod] = useState('')

  // Button State
  const [autoAdjustedFields, setAutoAdjustedFields] = useState([])
  const [scheduleApproved, setScheduleApproved] = useState(false)
  const [approvingSchedule, setApprovingSchedule] = useState(false)
  // Determines the visibility and activity state of the 'Finalize' button
  // - true: Schedule has been generated; 'Finalize' button should be active
  // - false: Schedule has not been generated; 'Finalize' button should be inactive
  const [scheduleGenerated, setScheduleGenerated] = useState(true)
  const [generatingSchedule, setGeneratingSchedule] = useState(false)
  const [sendingLoanAgreement, setSendingLoanAgreement] = useState(false)
  const [isDeclinedModalOpen, setDeclinedModalOpen] = useState(false)
  const [savingDeclined, setSavingDeclined] = useState(false)

  // Controls the execution of the useEffect hook to skip its some invocations
  const [ignoreEffect, setIgnoreEffect] = useState(true);

  // Program State
  const [programOptions, setProgramOptions] = useState([])
  const [programDetails, setProgramDetails] = useState({})

  // Fields to watch for page changes (Manager Approval, Helper Text, Forcing Schedule Generation)
  const watchLoanAmount = watch('loan_amount');
  const watchApr = watch('apr');
  const watchProgram = watch('program_id');
  const watchPaymentMethod = watch('payment_method_dropdown_option_id')
  const watchLoanFundingMethod = watch('loan_funding_method_dropdown_option_id')

  // Loan Term State
  const [minPrincipal, setMinPrincipal] = useState({ value: 0, source: '' })
  const [maxPrincipal, setMaxPrincipal] = useState({ value: 0, source: '' })
  const [minApr, setMinApr] = useState({ value: 0, source: '' })
  const [maxApr, setMaxApr] = useState({ value: 0, source: '' })
  const [maxPaymentAmount, setMaxPaymentAmount] = useState({ value: 0, source: '' })
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [loanFundingMethod, setLoanFundingMethod] = useState({});
  const [warningLoanAmount, setWarningLoanAmount] = useState("");
  const [warningApr, setWarningApr] = useState("");
  const [aboveMaxPaymentAmount, setAboveMaxPaymentAmount] = useState(false)
  const loanFundingMethodRealTime = dropdownOptions.find((o) => o?.code_key === 'loan_funding_method_realtime')
  const [availableLoanFundingMethods, setAvailableLoanFundingMethods] = useState(dropdownOptions.filter((o) => paymentScheduleData.available_loan_funding_methods?.includes(o?.id)).map((o) => ({ label: o.name, value: o.id })));

  const [portfolioHasRealtimePayment, setPortfolioHasRealtimePayment] = useState(paymentScheduleData?.portfolio_has_real_time_payment)

  useEffect(() => {
    if (paymentMethods?.length > 0) {
      let paymentMethodSelections = paymentMethods.map((o) => ({ label: o.name, value: o.id }));
      setPaymentMethodOptions(paymentMethodSelections)
    }
  }, [paymentMethods])

  /**
   * To avoid carrying a piece of state up in Verification.jsx above_max_payment is added into the payment schedule
   *  detail data from the processVerificationQuestions call
   */
  useEffect(() => {
    if (dropdownOptions && paymentScheduleDetails) {
      setAboveMaxPaymentAmount(paymentScheduleDetails?.above_max_payment)
      setEffectiveDate(`${paymentScheduleDetails?.effective_date}T00:00:00`)
      setLoanFundingMethod(dropdownOptions.find((x) => x?.id === +paymentScheduleDetails?.loan_funding_method_dropdown_option_id))

      const paymentMethodName = dropdownOptions.find((o) => o?.id === +paymentScheduleDetails.payment_method_dropdown_option_id)?.name
      setPaymentMethod(paymentMethodName)
      setValue('payment_method_dropdown_option_id', { label: paymentMethodName, value: paymentScheduleDetails.payment_method_dropdown_option_id })

      const defaultLoanFundingMethod = dropdownOptions.find((x) => x?.id === +paymentScheduleDetails?.loan_funding_method_dropdown_option_id)
      setValue('loan_funding_method_dropdown_option_id', { label: defaultLoanFundingMethod?.name, value: defaultLoanFundingMethod?.id })

      const loadTimeAdjustments = []
      if (originalEffectiveDate && originalEffectiveDate !== paymentScheduleDetails.effective_date) {
        loadTimeAdjustments.push({
          field: 'Effective Date',
          from: FormatISODate(originalEffectiveDate),
          to: FormatISODate(paymentScheduleDetails.effective_date),
        })
      }
      if (originalLoanFundingMethodId && String(originalLoanFundingMethodId) !== String(paymentScheduleDetails.loan_funding_method_dropdown_option_id)) {
        loadTimeAdjustments.push({
          field: 'Loan Funding Method',
          from: dropdownOptions.find((o) => o?.id === +originalLoanFundingMethodId)?.name,
          to: dropdownOptions.find((o) => o?.id === +paymentScheduleDetails.loan_funding_method_dropdown_option_id)?.name,
        })
      }
      if (loadTimeAdjustments.length > 0) {
        setAutoAdjustedFields(loadTimeAdjustments)
      }

      // We are setting into the form directly to handle cases for manager approval where we are re-rendering, but not re-generating a schedule
      setPaymentScheduleFormData({
        loan_amount: paymentScheduleDetails?.principal_amount,
        apr: paymentScheduleDetails?.apr,
        program_id: { value: paymentScheduleDetails?.program_id },
        payment_method_dropdown_option_id: { value: paymentScheduleDetails.payment_method_dropdown_option_id },
        loan_funding_method_dropdown_option_id: { value: paymentScheduleDetails?.loan_funding_method_dropdown_option_id },
      })
    }
  }, [dropdownOptions, paymentScheduleDetails, originalEffectiveDate, originalLoanFundingMethodId])

  /**
   * Set the initial loan terms based on which program is initially set on the lead
   */
  useEffect(() => {
    let minPrincipal = { value: 0, source: '' }
    let maxPrincipal = { value: 0, source: '' }
    let minApr = { value: 0, source: '' }
    let maxApr = { value: 0, source: '' }
    if (programDetails && Object.keys(programDetails).length > 0) {
      minPrincipal = { value: programDetails.min_principal, source: 'program' }
      maxPrincipal = { value: programDetails.max_principal, source: 'program' }
      minApr = { value: programDetails.min_apr, source: 'program' }
      maxApr = { value: programDetails.max_apr, source: 'program' }
    }

    if (ptiTerms && Object.keys(ptiTerms).length > 0) {
      if (ptiTerms.min_principal && parseInt(ptiTerms.min_principal, 10) > parseInt(minPrincipal.value, 10)) minPrincipal = { value: parseInt(ptiTerms.min_principal, 10), source: 'pti' }
      if (ptiTerms.max_principal && parseInt(ptiTerms.max_principal, 10) < parseInt(maxPrincipal.value, 10)) maxPrincipal = { value: parseInt(ptiTerms.max_principal, 10), source: 'pti' }
      if (ptiTerms.min_apr && ptiTerms.min_apr > minApr.value) minApr = { value: ptiTerms.min_apr, source: 'pti' }
      if (ptiTerms.max_apr && ptiTerms.max_apr < maxApr.value) maxApr = { value: ptiTerms.max_apr, source: 'pti' }
      if (ptiTerms.max_payment_amount) setMaxPaymentAmount({ value: parseInt(ptiTerms.max_payment_amount, 10), source: 'pti' })
    }

    setMinPrincipal(minPrincipal)
    setMaxPrincipal(maxPrincipal)
    setMinApr(minApr)
    setMaxApr(maxApr)
  }, [programDetails, ptiTerms])

  /**
   * Create the list of program options with a custom label
   */
  useEffect(() => {
    if (programs.length > 0) {
      let programSelections = programs.map((o) => ({ label: o.name, value: o.id }));
      setProgramOptions(programSelections)
      let program = programSelections?.find((lap) => +lap.value === +paymentScheduleDetails?.program_id)
      setProgramDetails(program)
      setValue('program_id', program ?? "")
    }
  }, [programs])

  /**
   * Whenever the program changes, set the program details state, so that Agent can see the program details.
   */
  useEffect(() => {
    const program = programs.find((p) => p.id === watchProgram?.value)
    setProgramDetails(program)
  }, [watchProgram])

  /**
   * When the Principal or APR changes check to see if it has gone outside the bounds of PTI. If it has, show the icon
   *  warning the Agent that the change will require manager approval.
   * NOTE: The plus sign before each value in the if statements is used for integer type conversion.
   */
  useEffect(() => {
    if (!isPrequalified && ((minPrincipal.source === 'pti' && +watchLoanAmount < +minPrincipal.value) || (maxPrincipal.source === 'pti' && +watchLoanAmount > +maxPrincipal.value))) {
      setWarningLoanAmount(<ManagerApprovalIcons type="mgr_approval_status_required" />);
    } else {
      setWarningLoanAmount("");
    }

    if (!isPrequalified && ((minApr.source === 'pti' && +watchApr < +minApr.value) || (maxApr.source === 'pti' && +watchApr > +maxApr.value))) {
      setWarningApr(<ManagerApprovalIcons type="mgr_approval_status_required" />);
    } else {
      setWarningApr("");
    }
  }, [watchLoanAmount, watchApr, minPrincipal, maxPrincipal, minApr, maxApr])

  /**
   * When any of the form elements change for the loan terms, force the agent to regenerate the schedule.
   */
  useEffect(() => {
    if (ignoreEffect) {
      setIgnoreEffect(false);
      return;
    }

    // Monitors changes in loan amount, APR, program, and payment method variables using Watch.
    // 'effectiveDate' is handled separately and should match the selected date from PaymentDatePicker.
    // Watches 'loan funding method' if real-time funding is available (dropdown-based selection).
    if (scheduleGenerated && (
      watchLoanAmount ||
      watchApr ||
      watchProgram ||
      watchPaymentMethod ||
      effectiveDate || (
        isRealtimeFundingAvailable &&
        watchLoanFundingMethod)
    )
    ) {
      setScheduleGenerated(false)
    }
  }, [watchLoanAmount, watchApr, watchProgram, watchPaymentMethod, effectiveDate, watchLoanFundingMethod])

  /**
   * Handle all side effects that should occur on this page as a result of the manager approval changing
   */
  useEffect(() => {
    const mgrApprovalStatus = dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key
    const mgrApprovalType = dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_type_dropdown_option_id)?.code_key

    if (mgrApprovalType === 'mgr_approval_type_lead_term_override' && mgrApprovalStatus === 'mgr_approval_status_approved') {
      setScheduleGenerated(true)
      setScheduleApproved(true)
    }
  }, [managerApproval])

  /**
   * When errors occur, call the dispatchError function to show a notification.
   */
  useEffect(() => {
    dispatchError()
  }, [errors, generatingSchedule])

  const dispatchError = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, [])
      dispatch(setNotification({ type: 'error', body: body }))
    }
  }

  const onSubmit = async (formData) => {
    setGeneratingSchedule(true)
    setAutoAdjustedFields([])

    let formError = false

    if (formData['loan_amount'] === '') {
      setError("loan_amount", { type: "manual", message: "Loan Amount is required" });
      formError = true
    }

    if (!formData['effective_date']) {
      setError("effective_date", { type: "manual", message: " is required" });
      formError = true
    }

    if (formData['apr'] === '') {
      setError("apr", { type: "manual", message: "APR is required" });
      formError = true
    }

    if (formData['program_id'] === '') {
      setError("program_id", { type: "manual", message: "Program is required" });
      formError = true
    }

    if (formData['payment_method_dropdown_option_id'] === '') {
      setError("payment_method_dropdown_option_id", { type: "manual", message: "Payment Method is required" });
      formError = true
    }

    if (formData['loan_funding_method_dropdown_option_id'] === '') {
      setError("loan_funding_method_dropdown_option_id", { type: "manual", message: "Loan Funding Method is required" });
      formError = true
    }

    if (formError) {
      setGeneratingSchedule(false)
      dispatchError()
      setHasFormError(true)
      return
    } else {
      setHasFormError(false)
    }

    setPaymentScheduleFormData(formData)

    const sanitizedData = {
      customer_id: customerData.id,
      verification_id: verificationId,
      loan_amount: formData['loan_amount'],
      program_id: formData['program_id']?.value,
      payment_method_dropdown_option_id: formData['payment_method_dropdown_option_id']?.value,
      origination_date: today,
      is_agent_apr_talkoff: (+watchApr < +paymentScheduleDetails?.apr),
      apr: formData['apr'],
      effective_date: effectiveDate ? format(new Date(effectiveDate), 'yyyy-MM-dd') : formData['effective_date'],
      waive_real_time_fee: formData['waive_real_time_fee'],
      loan_funding_method_dropdown_option_id: formData['loan_funding_method_dropdown_option_id']?.value,
    };

    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${sanitizedData['customer_id']}/generate_schedule`)
    try {
      const response = await requests.post(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: '' }))

        const responseData = response.data.data
        setPaymentScheduleData(responseData)
        setPtiTerms(responseData.pti_terms)
        setAboveMaxPaymentAmount(responseData.above_max_payment)
        setLoanFundingMethod(dropdownOptions.find((x) => x?.id === +responseData.loan_funding_method_dropdown_option_id))
        setEffectiveDate(`${responseData?.payment_schedule?.effective_date}T00:00:00`)

        // Update the payment method and ignore the subsequent useEffect call to prevent loops or redundant operations.
        setIgnoreEffect(true);

        const paymentMethodName = dropdownOptions.find((o) => o?.id === +responseData.payment_method_dropdown_option_id)?.name
        setPaymentMethod(paymentMethodName)
        setValue('payment_method_dropdown_option_id', { label: paymentMethodName, value: responseData.payment_method_dropdown_option_id })

        const defaultLoanFundingMethod = dropdownOptions.find((x) => x?.id === +responseData.loan_funding_method_dropdown_option_id)
        setValue('loan_funding_method_dropdown_option_id', { label: defaultLoanFundingMethod?.name, value: defaultLoanFundingMethod?.id })

        setScheduleGenerated(true)
        setManagerApproval({})
        setManagerApprovalPoll(false)

        setAutoAdjustedFields(computeScheduleAdjustments({
          sanitizedData,
          responseData,
          dropdownOptions,
          programDetails,
          bounds: { minPrincipal, maxPrincipal, minApr, maxApr, maxPaymentAmount },
        }))

        // Update the available loan funding methods
        setAvailableLoanFundingMethods(dropdownOptions.filter((o) => responseData.available_loan_funding_methods?.includes(o?.id)).map((o) => ({ label: o.name, value: o.id })));
        setIsRealtimeFundingAvailable(responseData.is_realtime_funding_available)
        setPortfolioHasRealtimePayment(responseData.portfolio_has_real_time_payment)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      setHasFormError(true)
    } finally {
      setScheduleApproved(false)
      setGeneratingSchedule(false)
    }
  };

  const sendApprovedSchedule = async () => {
    setApprovingSchedule(true)
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/approve_schedule`)

    try {
      const requestData = {
        effective_date: format(new Date(effectiveDate), 'yyyy-MM-dd'),
        approved_principal: paymentScheduleFormData.loan_amount,
        apr: paymentScheduleFormData.apr,
        program_id: paymentScheduleFormData.program_id?.value,
        payment_method_dropdown_option_id: paymentScheduleFormData.payment_method_dropdown_option_id.value,
        loan_funding_method_dropdown_option_id: paymentScheduleData.loan_funding_method_dropdown_option_id ?? paymentScheduleFormData.loan_funding_method_dropdown_option_id?.value,
        interest_rate: paymentScheduleData.payment_schedule.interest_rate, // Last generated interest rate
        ...(paymentScheduleFormData.waive_real_time_fee !== undefined && { waive_real_time_fee: paymentScheduleFormData.waive_real_time_fee }),
      }
      const response = await requests.post(requestData)

      // If manager approval is required start polling the BE
      setManagerApproval(response?.data?.data)
      const managerApprovalStatus = dropdownOptions.find((o) => o?.id === response?.data?.data?.mgr_approval_status_dropdown_option_id)?.code_key
      if (managerApprovalStatus === 'mgr_approval_status_required') {
        setManagerApprovalPoll(true)
      } else {
        // If manager approval is not required mark the schedule as approved, so that the agent can proceed
        setScheduleApproved(true)
      }
    } catch (error) {
      setScheduleGenerated(false)
      handleErrorResponse(error.response?.data?.errors, setError)
    } finally {
      setApprovingSchedule(false)
    }
  }

  const sendLoanAgreement = async () => {
    setSendingLoanAgreement(true)
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/send_loan_agreement`)
    try {
      const requestData = {
        customer_id: customerData.id,
        approved_principal: paymentScheduleFormData.loan_amount,
        apr: paymentScheduleFormData.apr,
        program_id: paymentScheduleFormData.program_id?.value,
        payment_method_dropdown_option_id: paymentScheduleFormData.payment_method_dropdown_option_id.value
      }
      const response = await requests.post(requestData)
      setLoanData(response.data);
      setStep('loan_agreement')
      setManagerApprovalPoll(false)
    } catch (e) {
      dispatch(setNotification({ type: 'error', body: e.response?.data?.message }))
    } finally {
      setSendingLoanAgreement(false)
    }
  }

  const offeredPaymentAmount = getOfferedPaymentAmount(paymentScheduleData)
  const hasGeneratedSchedule = offeredPaymentAmount !== null
  const idealPaymentAmountOptions = buildIdealPaymentAmountOptions(offeredPaymentAmount)

  const saveCustomerDeclined = async (idealPaymentAmount) => {
    setSavingDeclined(true)
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manual_verification/${customerData.id}/customer_declined`)
    try {
      await requests.post({
        verification_id: verificationId,
        ideal_payment_amount: idealPaymentAmount,
        offered_payment_amount: offeredPaymentAmount,
      })
      dispatch(setNotification({ type: 'success', body: 'Customer declined payment recorded.' }))
      setDeclinedModalOpen(false)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message ?? 'Failed to record the declined payment.' }))
    } finally {
      setSavingDeclined(false)
    }
  }

  const renderFormUI = () => {
    return (
      <>
        <div className="field is-horizontal">
          <div className="field-label is-normal verification-margin" style={{ flex: 1 }}>
            <label>Effective Date:</label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <Controller
                  name="effective_date"
                  control={control}
                  defaultValue={paymentScheduleDetails.effective_date}
                  render={({ field: { value, onChange } }) => (
                    <PaymentDatePicker
                      paymentMethodDdoId={paymentScheduleDetails?.loan_funding_method_dropdown_option_id}
                      max={`${paymentScheduleDetails?.max_effective_date}T00:00:00`}
                      min={`${paymentScheduleDetails?.current_date}T00:00:00`}
                      selectedDate={effectiveDate}
                      setSelectedDate={setEffectiveDate}
                      isDisabled={isReadOnly}
                    />
                  )}
                />
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.effective_date &&
                    <span>{errors.effective_date.message ? errors.effective_date.message : 'Loan Amount is required'}</span>}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="field is-horizontal">
          <div className="field-label verification-margin" style={{ flex: 1 }}>
            <label>Loan Funding Method:</label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="loan_funding_method_dropdown_option_id"
                    control={control}
                    // defaultValue={{ label: loanFundingMethod?.name, value: loanFundingMethod?.id }}
                    render={({ field }) => (
                      <Select
                        name="loan_funding_method_dropdown_option_id"
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={availableLoanFundingMethods}
                        isSearchable={false}
                        isDisabled={isReadOnly}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.loan_funding_method_dropdown_option_id &&
                      <span>{errors.loan_funding_method_dropdown_option_id.message ? errors.loan_funding_method_dropdown_option_id.message : 'Number of Payments is required'}</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="field is-horizontal">
          <h3 className="has-text-danger m-1">
            {getFundingMessage(isRealtimeFundingAvailable, portfolioHasRealtimePayment)}
          </h3>
        </div>

        <div className="field is-horizontal">
          <div className="field-label is-normal verification-margin" style={{ flex: 1 }}>
            <label>{warningLoanAmount} Principal:
              <span
                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                data-tooltip={`The min/max for principal is displayed below the input box. You can request manager approval outside the bounds of PTI, but not of the Program.`}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
              </span>
            </label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <Controller
                  name="loan_amount"
                  control={control}
                  defaultValue={paymentScheduleDetails.principal_amount}
                  disabled={isReadOnly}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      form="verification_payment_schedule_form"
                      min={isPrequalified ? prequalifiedData?.min_principal_amount : programDetails?.min_principal}
                      max={isPrequalified ? prequalifiedData?.max_principal_amount : programDetails?.max_principal}
                      onChange={(e) => {
                        // return only the integer to whole numbers
                        const value = Math.trunc(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.loan_amount &&
                    <span>{errors.loan_amount.message ? errors.loan_amount.message : 'Loan Amount is required'}</span>}
                </p>
              </div>
              <p className="help">
                {isPrequalified ? (
                  <>
                    <span>Min: ${prequalifiedData?.min_principal_amount}</span>
                    &nbsp;-&nbsp;
                    <span>Max: ${prequalifiedData?.max_principal_amount}</span>
                  </>
                ) : (
                  <>
                    {minPrincipal.source === 'program' ? (
                      <span className="has-text-link">Min: ${minPrincipal.value} (Program)</span>
                    ) : (
                      <span>Min: ${minPrincipal.value} (PTI)</span>
                    )}
                    &nbsp;-&nbsp;
                    {maxPrincipal.source === 'program' ? (
                      <span className="has-text-link">Max: ${maxPrincipal.value} (Program)</span>
                    ) : (
                      <span>Max: ${maxPrincipal.value} (PTI)</span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className={`field is-horizontal`}>
          <div className="field-label is-normal verification-margin" style={{ flex: 1 }}>
            <label>{warningApr} APR:
              <span
                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                data-tooltip={`The min/max for APR is displayed below the input box. You can request manager approval outside the bounds of PTI, but not of the Program.`}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
              </span>
            </label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <Controller
                  name="apr"
                  control={control}
                  defaultValue={paymentScheduleDetails?.apr}
                  disabled={isReadOnly}
                  render={({ field }) => (
                    <input
                      {...field}
                      className="input is-small"
                      type="number"
                      step="0.001"
                      form="verification_payment_schedule_form"
                      min={programDetails?.min_apr}
                      max={programDetails?.max_apr}
                    />
                  )}
                />
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.apr && <span>{errors.apr.message ? errors.apr.message : 'APR is required'}</span>}
                </p>
              </div>
              <p className="help">
                {minApr.source === 'program' ? (
                  <span className="has-text-link">Min: <PercentFormat value={minApr.value} fractionDigits={3} /> (Program)</span>
                ) : (
                  <span>Min: <PercentFormat value={minApr.value} fractionDigits={3} /> (PTI)</span>
                )}
                &nbsp;-&nbsp;
                {maxApr.source === 'program' ? (
                  <span className="has-text-link">Max: <PercentFormat value={maxApr.value} fractionDigits={3} /> (Program)</span>
                ) : (
                  <span>Max: <PercentFormat value={maxApr.value} fractionDigits={3} /> (PTI)</span>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="field is-horizontal">
          <div className="field-label is-normal verification-margin" style={{ flex: 1 }}>
            <label>Program:</label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <Controller
                  name="program_id"
                  control={control}
                  defaultValue={paymentScheduleDetails?.program_id}
                  render={({ field }) => (
                    <Select
                      {...field}
                      classNames={reactSelectClassNames()}
                      options={programOptions}
                      isSearchable={false}
                      isDisabled={isReadOnly}
                    />
                  )}
                />
                <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                  {errors.program_id &&
                    <span>{errors.program_id.message ? errors.program_id.message : 'Program is required'}</span>}
                </p>
              </div>
              {programDetails && !isPrequalified && (
                <>
                  <p className="help">Min APR: <PercentFormat value={programDetails?.min_apr} fractionDigits={3} />{" - "}Max APR: <PercentFormat value={programDetails?.max_apr} fractionDigits={3} /></p>
                  <p
                    className="help"
                  >{`Min Principal: $${programDetails?.min_principal} - Max Principal: $${programDetails?.max_principal}`}</p>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="field is-horizontal">
          <div className="field-label is-normal verification-margin" style={{ flex: 1 }}>
            <label>Payment Method:
              <span
                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                data-tooltip={`This payment method is how the customer will be paying us for regularly scheduled payments. This is NOT the funding method.`}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
              </span>
            </label>
          </div>
          <div className="field-body" style={{ flex: '3 1 0%' }}>
            <div className="field">
              <div className="control">
                <div style={{ width: '100%' }}>
                  <Controller
                    name="payment_method_dropdown_option_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        name="payment_method_dropdown_option_id"
                        {...field}
                        classNames={reactSelectClassNames()}
                        options={paymentMethodOptions}
                        isSearchable={false}
                        isDisabled={isReadOnly}
                      />
                    )}
                  />
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.payment_method_dropdown_option_id &&
                      <span>{errors.payment_method_dropdown_option_id.message ? errors.payment_method_dropdown_option_id.message : 'Number of Payments is required'}</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="field is-horizontal">
          <div className="field-label verification-margin" style={{ flex: 1 }}>
            <label>Max Previous Rate:</label>
          </div>
          <div className="field-body is-align-items-center" style={{ flex: '3 1 0%' }}>
            <p>N/A</p>
          </div>
        </div>

        <div className="field is-horizontal">
          <div className="field-label verification-margin" style={{ flex: 1 }}>
            <label>{!isPrequalified && aboveMaxPaymentAmount && <ManagerApprovalIcons type="mgr_approval_status_required" />} Max Payment
              Amount:</label>
          </div>
          <div className="field-body is-align-items-center" style={{ flex: '3 1 0%' }}>
            <p>{maxPaymentAmount.value ? `$${maxPaymentAmount.value}` : 'N/A'}</p>
          </div>
        </div>

        <div className="field is-horizontal">
          <div className="field-label verification-margin" style={{ flex: 1 }}>
            <label>APR Talk Off:
              <span
                className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                data-tooltip={`If the APR is manually lowered at all from the original APR then the lead will be marked as APR Talk Off.`}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
              </span>
            </label>
          </div>
          <div className="field-body is-align-items-center" style={{ flex: '3 1 0%' }}>
            <div className="control is-size-7 is-flex mt-1">
              <label className="is-flex">
                <Controller
                  name="is_agent_apr_talkoff"
                  control={control}
                  disabled={isReadOnly}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="checkbox"
                      className="mr-1"
                      checked={+watchApr < +paymentScheduleDetails?.apr}
                      disabled
                      form="verification_payment_schedule_form"
                    />
                  )}
                />
              </label>
            </div>
          </div>
        </div>

        {/* currently we are not going to use this feature, but can be added in future */}
        {/* {watchLoanFundingMethod?.value === loanFundingMethodRealTime?.id && (
          <div className="field is-horizontal">
            <div className="field-label verification-margin" style={{ flex: 1 }}>
              <label>Waive Real Time Fee:
                <span
                  className={'lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left'}
                  data-tooltip={`Checking this will waive the loan fee associated with real-time funding. It should only be used if required to secure the loan.`}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className="has-text-link" />
                </span>
              </label>
            </div>
            <div className="field-body is-align-items-center" style={{ flex: '3 1 0%' }}>
              <div className="control is-size-7 is-flex mt-1">
                <label className="is-flex">
                  <Controller
                    name="waive_real_time_fee"
                    control={control}
                    disabled={isReadOnly}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="checkbox"
                        className="mr-1"
                        form="verification_payment_schedule_form"
                      />
                    )}
                  />
                </label>
              </div>
            </div>
          </div>
        )} */}

        <div className="column is-flex is-align-items-end p-0 m-0" style={{ justifyContent: 'end' }}>
          <button
            type="button"
            className="button is-danger is-small mr-1"
            data-testid="customer-declined-button"
            disabled={!hasGeneratedSchedule || generatingSchedule || approvingSchedule || sendingLoanAgreement || isReadOnly}
            onClick={() => setDeclinedModalOpen(true)}
          >
            Customer Declined
          </button>
          <button
            type="submit"
            className="button is-link is-small mr-1"
            disabled={generatingSchedule || approvingSchedule || sendingLoanAgreement || dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key === 'mgr_approval_status_required' || isReadOnly}
          >
            Generate Schedule
            <span
              className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
              data-tooltip={`Calculate a new schedule for the entered loan terms. The only time you cannot generate a new schedule is when there is a pending manager approval.`}
            >
              <FontAwesomeIcon icon={faCircleInfo} className={`pl-1`} />
            </span>
          </button>
        </div>

        <div className="divider mb-4 my-2" />

        <div className="column is-flex is-align-items-end p-0 m-0" style={{ justifyContent: 'space-between' }}>
          <button
            className="button has-background-grey has-text-light is-small"
            type="button"
            onClick={() => setStep('questions')}
          >
            {"<- Back"}
          </button>
          <span>
            {!scheduleApproved ? (
              <button
                className="button is-danger is-small"
                disabled={hasFormError || isReadOnly || approvingSchedule || !scheduleGenerated || dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key === 'mgr_approval_status_required'}
                onClick={sendApprovedSchedule}
              >
                {!isPrequalified && (warningApr || warningLoanAmount || aboveMaxPaymentAmount) ? (
                  <span className="mx-2">
                    Send for Manager Approval
                  </span>
                ) : (
                  <span className="mx-2">
                    Finalize Schedule
                    <span
                      className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                      data-tooltip={`This action runs the final checks on a lead before approving the generated schedule. This is where manager approval will get sent off if needed.`}
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className={`pl-1`} />
                    </span>
                  </span>
                )}
              </button>
            ) : (
              <button
                type="button"
                className="button is-danger is-small"
                disabled={sendingLoanAgreement || !scheduleApproved || hasFormError || isReadOnly || !scheduleGenerated}
                onClick={sendLoanAgreement}
              >
                <span className="mx-2">
                  Send Loan Agreement
                  <span
                    className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                    data-tooltip={`You can only send a loan agreement if the payment schedule has been finalized. This action will send an email to the customer containing a link to the loan signing page. It will also mark the verification process as complete which unblocks the customer in the SSV process.`}
                  >
                    <FontAwesomeIcon icon={faCircleInfo} className={`pl-1`} />
                  </span>
                </span>
              </button>
            )}
          </span>
        </div>
        {dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_type_dropdown_option_id)?.code_key === 'mgr_approval_type_lead_term_override' && (
          <div className="is-flex" style={{ marginTop: '5px', justifyContent: 'end' }}>
            <ManagerApprovalIcons
              type={dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key}
            />
            <p style={{ marginLeft: '5px' }}>
              {getManagerApprovalStatus(dropdownOptions.find((o) => o?.id === managerApproval?.mgr_approval_status_dropdown_option_id)?.code_key)}
            </p>
          </div>
        )}
      </>
    )
  }

  return (
    <div id="step2" data-testid="verification-step-2">
      <div className="lox-detail mt-1 mx-1">
        <div className="columns mb-0">
          <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
            <div className="title is-4 has-text-primary is-flex" style={{ whiteSpace: 'nowrap' }} data-testid="verification-step-2-heading">
              Verification Step 2: Loan Details
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="form lox-form" id="verification_payment_schedule_form">
          <div className="columns mb-0">
            <div className="column is-half">
              {renderFormUI()}
            </div>
            <div className="column is-half">
              <PaymentScheduleTable
                paymentScheduleData={paymentScheduleData}
                autoAdjustedFields={autoAdjustedFields}
                generatingSchedule={generatingSchedule}
                paymentMethod={paymentMethod}
              />
            </div>
          </div>
        </form>
        <CustomerDeclinedModal
          isOpen={isDeclinedModalOpen}
          offeredPaymentAmount={offeredPaymentAmount}
          idealPaymentAmountOptions={idealPaymentAmountOptions}
          isSaving={savingDeclined}
          onClose={() => setDeclinedModalOpen(false)}
          onSave={saveCustomerDeclined}
        />
      </div>
    </div>
  );
}
